import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  getCamera,
  getConfigs,
  getStats,
  updateCameraConfig,
  updateCameraShop,
  archiveDevices,
  unarchiveDevices,
} from "../network/api";
import ShopSelect from "./common/ShopSelect";
import {
  Alert,
  Card,
  Descriptions,
  Tag,
  // recharts also exports a `Tooltip` (used by the telemetry chart below).
  Tooltip as AntTooltip,
  Space,
  Button,
  Segmented,
  Modal,
  Select,
  Input,
  DatePicker,
  Empty,
  Spin,
  Row,
  Col,
} from "antd";
import {
  DesktopOutlined,
  ArrowLeftOutlined,
  PictureOutlined,
  UnorderedListOutlined,
  HeartOutlined,
  SettingOutlined,
  VideoCameraOutlined,
  FileTextOutlined,
  EditOutlined,
  ShopOutlined,
  AreaChartOutlined,
  InboxOutlined,
  UndoOutlined,
  CloudUploadOutlined,
} from "@ant-design/icons";
import PageHeader from "./common/PageHeader";
import VisitLog from "./common/VisitLog";
import PreventiveCard from "./PreventiveCard";
import DeviceControls from "./DeviceControls";
import { notify, serverError } from "../utils/notify";
import { relativeTime } from "../constants/health";
import {
  VERSION_STATUS,
  VERSION_STATUS_TAG_COLOR,
  VERSION_STATUS_LABEL,
  versionLabel,
  versionTooltip,
} from "../constants/agentVersion";
import { useAuth } from "../context/AuthContext";
import { BRAND } from "../theme/themeConfig";
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  Tooltip,
  CartesianGrid,
  ResponsiveContainer,
} from "recharts";
import dayjs from "dayjs";

const { RangePicker } = DatePicker;

const METRICS = [
  { label: "CPU %", value: "cpuUsage" },
  { label: "Temp °C", value: "cpuTemprature" },
  { label: "Memory", value: "memoryUsed" },
  { label: "Storage Used", value: "storageUsed" },
  { label: "Storage Total", value: "storageTotal" },
];

const DeviceDetails = () => {
  const { deviceId } = useParams();
  const navigate = useNavigate();
  const { isHardwareAdmin } = useAuth();

  const [device, setDevice] = useState(null);
  const [loadingDevice, setLoadingDevice] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [stats, setStats] = useState([]);
  const [loadingStats, setLoadingStats] = useState(false);
  const [selectedField, setSelectedField] = useState("cpuUsage");
  const [range, setRange] = useState(null);

  const [configs, setConfigs] = useState([]);
  const [assignOpen, setAssignOpen] = useState(false);
  const [assignValue, setAssignValue] = useState();
  const [assigning, setAssigning] = useState(false);

  const [shopOpen, setShopOpen] = useState(false);
  const [shopValue, setShopValue] = useState();
  const [assigningShop, setAssigningShop] = useState(false);

  const [archiveOpen, setArchiveOpen] = useState(false);
  const [archiveReason, setArchiveReason] = useState("");
  const [archiving, setArchiving] = useState(false);

  const fetchDeviceDetails = async () => {
    setLoadingDevice(true);
    try {
      const response = await getCamera(deviceId);
      setDevice(response || null);
      setNotFound(!response);
    } catch (error) {
      if (error?.response?.status === 404) setNotFound(true);
      else notify.error(serverError(error, "Failed to load device"));
      setDevice(null);
    } finally {
      setLoadingDevice(false);
    }
  };

  const fetchConfigs = async () => {
    try {
      const response = await getConfigs();
      setConfigs(Array.isArray(response) ? response : []);
    } catch (error) {
      setConfigs([]);
    }
  };


  const fetchStats = async (r) => {
    setLoadingStats(true);
    const fromIso = r?.[0] ? r[0].toISOString() : undefined;
    const toIso = r?.[1] ? r[1].toISOString() : undefined;
    try {
      const response = await getStats(deviceId, fromIso, toIso, 100);
      setStats(Array.isArray(response) ? response : []);
    } catch (error) {
      setStats([]);
    } finally {
      setLoadingStats(false);
    }
  };

  useEffect(() => {
    if (deviceId) {
      fetchDeviceDetails();
      fetchConfigs();
      fetchStats();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [deviceId]);

  const chartData = [...stats].reverse().map((item) => ({
    time: dayjs(item.createdAt).format("MM/DD HH:mm"),
    value: item[selectedField],
  }));

  const openAssign = () => {
    setAssignValue(device?.config?._id);
    setAssignOpen(true);
  };

  const submitAssign = async () => {
    if (!assignValue) return notify.warning("Select a configuration first");
    setAssigning(true);
    try {
      await updateCameraConfig(device._id, assignValue);
      notify.success("Configuration assigned");
      setAssignOpen(false);
      fetchDeviceDetails();
    } catch (error) {
      notify.error(serverError(error, "Failed to assign configuration"));
    } finally {
      setAssigning(false);
    }
  };

  const openShop = () => {
    setShopValue(device?.shop?._id);
    setShopOpen(true);
  };

  const submitShop = async () => {
    setAssigningShop(true);
    try {
      await updateCameraShop(device._id, shopValue || null);
      notify.success(shopValue ? "Outlet assigned" : "Outlet cleared");
      setShopOpen(false);
      fetchDeviceDetails();
    } catch (error) {
      notify.error(serverError(error, "Failed to assign outlet"));
    } finally {
      setAssigningShop(false);
    }
  };

  const submitArchive = async () => {
    setArchiving(true);
    try {
      await archiveDevices({ ids: [device._id], reason: archiveReason || undefined });
      notify.success("Device archived");
      setArchiveOpen(false);
      setArchiveReason("");
      fetchDeviceDetails();
    } catch (error) {
      notify.error(serverError(error, "Failed to archive device"));
    } finally {
      setArchiving(false);
    }
  };

  const submitRestore = async () => {
    setArchiving(true);
    try {
      await unarchiveDevices({ ids: [device._id] });
      notify.success("Device restored to the live fleet");
      fetchDeviceDetails();
    } catch (error) {
      notify.error(serverError(error, "Failed to restore device"));
    } finally {
      setArchiving(false);
    }
  };

  const go = (path) => () => navigate(path);
  const openExternal = (port) => () => {
    if (device?.currentIp) window.open(`http://${device.currentIp}:${port}`, "_blank");
  };

  if (loadingDevice) {
    return (
      <div style={{ padding: 60, textAlign: "center" }}>
        <Spin size="large" />
      </div>
    );
  }

  if (notFound || !device) {
    return (
      <div>
        <Button type="link" icon={<ArrowLeftOutlined />} onClick={go("/dashboard/devices")} style={{ paddingLeft: 0, marginBottom: 12 }}>
          Back to Devices
        </Button>
        <Empty description="Device not found" />
      </div>
    );
  }

  const dev = device.deviceId;
  const av = device.agentVersion;
  const avStatus = av?.status || VERSION_STATUS.UNKNOWN;

  const infoItems = [
    { key: "id", label: "Device ID", children: <span style={{ fontWeight: 600 }}>{device.deviceId}</span> },
    { key: "title", label: "Title", children: device.deviceTitle || "—" },
    {
      key: "agentVersion",
      label: "Agent version",
      children: (
        <Space wrap size={8}>
          <AntTooltip title={versionTooltip(av)}>
            <Tag color={VERSION_STATUS_TAG_COLOR[avStatus]} style={{ marginInlineEnd: 0 }}>
              {versionLabel(av)}
            </Tag>
          </AntTooltip>
          <span style={{ color: "#5b6b85", fontSize: 12 }}>
            {VERSION_STATUS_LABEL[avStatus]}
            {av?.appliedAt ? ` · applied ${relativeTime(av.appliedAt)}` : ""}
          </span>
          {avStatus === VERSION_STATUS.OUTDATED && av?.target?.version && (
            <Tag icon={<CloudUploadOutlined />} color="processing">
              target {av.target.version}
            </Tag>
          )}
        </Space>
      ),
    },
    {
      key: "config",
      label: "Configuration",
      children: (
        <Space wrap>
          {device.config?.title ? <Tag color="blue">{device.config.title}</Tag> : <Tag>Unassigned</Tag>}
          {device.config?._id && (
            <Button size="small" type="link" icon={<SettingOutlined />} onClick={go(`/dashboard/config-details/${device.config._id}`)}>
              View
            </Button>
          )}
          <Button size="small" type="link" icon={<EditOutlined />} onClick={openAssign}>
            Assign
          </Button>
        </Space>
      ),
    },
    {
      key: "shop",
      label: "Outlet",
      children: (
        <Space wrap>
          {device.shop?.title ? <Tag color="purple">{device.shop.title}</Tag> : <Tag>Unassigned</Tag>}
          {device.shop?._id && (
            <Button size="small" type="link" icon={<ShopOutlined />} onClick={go(`/dashboard/shop-details/${device.shop._id}`)}>
              View
            </Button>
          )}
          <Button size="small" type="link" icon={<EditOutlined />} onClick={openShop}>
            Assign
          </Button>
        </Space>
      ),
    },
    { key: "net", label: "Network", children: device.currentNetwork ? `${device.currentNetwork}  ·  ${device.currentIp || "—"}` : "—" },
    {
      key: "publicIp",
      label: "Public IP",
      children: device.publicIp
        ? <span style={{ fontFamily: "monospace" }}>{device.publicIp} <AntTooltip title="The device's WAN/public IP as seen by DMS on its last heartbeat"><Tag style={{ marginLeft: 6 }}>WAN</Tag></AntTooltip></span>
        : "—",
    },
    { key: "created", label: "First seen", children: device.createdAt ? dayjs(device.createdAt).format("YYYY-MM-DD HH:mm") : "—" },
    { key: "updated", label: "Last update", children: device.updatedAt ? `${dayjs(device.updatedAt).format("YYYY-MM-DD HH:mm")} (${relativeTime(device.updatedAt)})` : "—" },
    ...(device.onboardedBy?.name || device.onboardedBy?.user?.name
      ? [{
          key: "onboardedBy",
          label: "Onboarded by",
          children: `${device.onboardedBy.user?.name || device.onboardedBy.name}${device.onboardedBy.at ? ` · ${relativeTime(device.onboardedBy.at)}` : ""}`,
        }]
      : []),
    ...(device.archivedAt
      ? [{
          key: "archived",
          label: "Archived",
          children: `${dayjs(device.archivedAt).format("YYYY-MM-DD HH:mm")}${
            device.archivedBy?.user?.name || device.archivedBy?.name
              ? ` · by ${device.archivedBy.user?.name || device.archivedBy.name}`
              : ""
          }${device.archiveReason ? ` · ${device.archiveReason}` : ""}`,
        }]
      : []),
  ];

  return (
    <div>
      <PageHeader
        title="Device Details"
        subtitle={device.deviceTitle || device.deviceId}
        icon={<DesktopOutlined />}
        extra={
          <>
            {isHardwareAdmin &&
              (device.archivedAt ? (
                <Button icon={<UndoOutlined />} loading={archiving} onClick={submitRestore}>
                  Restore to fleet
                </Button>
              ) : (
                <Button danger icon={<InboxOutlined />} onClick={() => setArchiveOpen(true)}>
                  Archive
                </Button>
              ))}
            <Button icon={<ArrowLeftOutlined />} onClick={go("/dashboard/devices")}>
              Back to Devices
            </Button>
          </>
        }
      />

      {device.archivedAt && (
        <Alert
          type="warning"
          showIcon
          style={{ marginBottom: 16 }}
          message="This device is archived"
          description="It is excluded from the live fleet — no health monitoring, preventive scheduling or agent rollouts. Its history is preserved and it can be restored at any time."
        />
      )}

      <Row gutter={[16, 16]}>
        <Col xs={24} lg={14}>
          <Card title="Device Information" style={{ height: "100%" }}>
            <Descriptions column={1} bordered size="small" items={infoItems} />
          </Card>
        </Col>

        <Col xs={24} lg={10}>
          <Card title="Quick Actions" style={{ height: "100%" }}>
            <Space direction="vertical" style={{ width: "100%" }} size={10}>
              <Space wrap>
                <Button icon={<HeartOutlined />} onClick={go(`/dashboard/health/${dev}`)}>Health</Button>
                <Button icon={<AreaChartOutlined />} onClick={go(`/dashboard/footfall/${dev}`)}>Footfall</Button>
                <Button icon={<PictureOutlined />} onClick={go(`/dashboard/pictures/${dev}`)}>Pictures</Button>
                <Button icon={<UnorderedListOutlined />} onClick={go(`/dashboard/logs/${dev}`)}>Logs</Button>
              </Space>
              <Space wrap>
                <Button icon={<VideoCameraOutlined />} disabled={!device.currentIp} onClick={openExternal(8080)}>
                  Live Camera
                </Button>
                <Button icon={<FileTextOutlined />} disabled={!device.currentIp} onClick={openExternal(8090)}>
                  Live Logs
                </Button>
              </Space>
            </Space>
          </Card>
        </Col>

        <Col span={24}>
          <Card
            title="Telemetry"
            extra={
              <Space wrap>
                <Segmented size="small" options={METRICS} value={selectedField} onChange={setSelectedField} />
                <RangePicker
                  showTime
                  size="small"
                  format="YYYY-MM-DD HH:mm"
                  value={range}
                  onChange={setRange}
                />
                <Button size="small" type="primary" onClick={() => fetchStats(range)}>
                  Apply
                </Button>
              </Space>
            }
          >
            {chartData.length === 0 ? (
              <Empty description="No telemetry in range" style={{ padding: 40 }} />
            ) : (
              <div style={{ height: 360 }}>
                <Spin spinning={loadingStats}>
                  <ResponsiveContainer width="100%" height={360}>
                    <LineChart data={chartData} margin={{ top: 10, right: 20, bottom: 0, left: -10 }}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#eef1f6" />
                      <XAxis dataKey="time" tick={{ fontSize: 11 }} minTickGap={28} />
                      <YAxis tick={{ fontSize: 11 }} />
                      <Tooltip />
                      <Line type="monotone" dataKey="value" name={METRICS.find((m) => m.value === selectedField)?.label} stroke={BRAND.primary} strokeWidth={2} dot={false} />
                    </LineChart>
                  </ResponsiveContainer>
                </Spin>
              </div>
            )}
          </Card>
        </Col>

        <Col span={24}>
          <DeviceControls deviceId={device.deviceId} />
        </Col>

        <Col span={24}>
          <PreventiveCard device={device} onChange={fetchDeviceDetails} />
        </Col>

        <Col span={24}>
          <VisitLog deviceId={device.deviceId} title="Visit & maintenance log" />
        </Col>
      </Row>

      <Modal
        title="Assign Configuration"
        open={assignOpen}
        onOk={submitAssign}
        confirmLoading={assigning}
        okText="Assign"
        onCancel={() => setAssignOpen(false)}
      >
        <p style={{ color: "#5b6b85" }}>Select a configuration to apply to this device.</p>
        <Select
          style={{ width: "100%" }}
          placeholder="Select a configuration"
          value={assignValue}
          onChange={setAssignValue}
          options={configs.map((c) => ({ value: c._id, label: c.title }))}
          notFoundContent={configs.length === 0 ? "No configurations available" : undefined}
        />
      </Modal>

      <Modal
        title="Archive this device"
        open={archiveOpen}
        onOk={submitArchive}
        confirmLoading={archiving}
        okText="Archive"
        okButtonProps={{ danger: true }}
        onCancel={() => setArchiveOpen(false)}
      >
        <p style={{ color: "#5b6b85" }}>
          {device.deviceTitle || device.deviceId} leaves the live fleet: no health monitoring,
          no preventive visits and no agent rollouts. Its history is kept and you can restore
          it at any time.
        </p>
        <Input
          placeholder="Reason (optional) — e.g. superseded test run"
          value={archiveReason}
          onChange={(e) => setArchiveReason(e.target.value)}
        />
      </Modal>

      <Modal
        title="Assign Outlet"
        open={shopOpen}
        onOk={submitShop}
        confirmLoading={assigningShop}
        okText="Save"
        onCancel={() => setShopOpen(false)}
      >
        <p style={{ color: "#5b6b85" }}>Place this device in a retail outlet.</p>
        <ShopSelect
          allowClear
          value={shopValue}
          onChange={setShopValue}
          current={device.shop}
          placeholder="Search outlets by code or name…"
        />
      </Modal>
    </div>
  );
};

export default DeviceDetails;
